import React, { useEffect, useRef, useState } from "react";
import {
  webLoadingFalse,
  webNavigate,
} from "../../services/navigate.service";
import UploadIcon from "../../images/UploadIcon";
import EditIconV2 from "../../images/EditIconV2";
import { getListAsset } from "../../services/asset.service";
import { useResponsive } from "../../hook/useResponsive";
import {
  ListColumn,
  ListQuery,
  ListRowAction,
  ListScreen,
  cells,
} from "../../components/list";

interface InforTable {
  title: string;
  path: string;
  desc: string;
}
interface AssetRow {
  assetCategories: { categoryName: string };
  id: string;
  No: string;
  assetName: string;
  categoryName: string;
  depreciationYears: string;
  search: string;
  qty: string;
  price: string;
  amount: number;
}
interface AssetsResponse {
  count: number;
  data: AssetRow[];
}

const toAmount = (value: any): number | null =>
  value === null || value === undefined || value === "" ? null : Number(value);

const Assets = () => {
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const companyId = localStorage.getItem("companyId");
  const preLanguage = localStorage.getItem("language");
  const [language, setLanguage] = useState<any>(null);
  const { isTouchLayout: isMobile } = useResponsive();
  const inforTable: InforTable = {
    title:
      language && language?.["No Assets Found"]
        ? language?.["No Assets Found"]
        : "No Assets Found",
    path: "/assets/new",
    desc:
      language &&
      language?.[
        "To display assets here, please first accept a purchase that includes asset items."
      ]
        ? language?.[
            "To display assets here, please first accept a purchase that includes asset items."
          ]
        : "To display assets here, please first accept a purchase that includes asset items.",
  };
  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);
  const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === "f") {
      event.preventDefault();
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  };
  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  /** The API searches asset name and category name. */
  const fetchAssets = async (query: ListQuery) => {
    webLoadingFalse();
    const response = await getListAsset(
      query.page,
      query.limit,
      query.search || undefined,
      companyId,
    );
    const value: AssetsResponse = response.data;
    return { rows: value?.data ?? [], count: value?.count ?? 0 };
  };

  const goToAsset = (id: string) => webNavigate("/" + preLanguage + "/assets/" + id);

  const columns: ListColumn<AssetRow>[] = [
    {
      key: "No",
      label: "No.",
      width: 70,
      value: (a) => String(a?.No ?? ""),
      mobile: "sheet",
    },
    {
      key: "assetName",
      label: "Title",
      width: 260,
      hideable: false,
      value: (a) => a?.assetName ?? "",
      mobile: "title",
      onClick: (a) => goToAsset(String(a.id)),
    },
    {
      key: "category",
      label: "Category",
      width: 180,
      value: (a) => a?.assetCategories?.categoryName ?? "",
      mobile: "subtitle",
    },
    {
      key: "depreciationYears",
      label: "Impairment",
      width: 120,
      value: (a) => `${a?.depreciationYears ?? ""} ${language?.["years"] ?? "years"}`,
      mobile: "sheet",
    },
    {
      key: "qty",
      label: "QTY",
      width: 80,
      value: (a) => a?.qty ?? "",
      mobile: "sheet",
    },
    {
      key: "price",
      label: "Price",
      width: 120,
      align: "right",
      render: cells.money((a) => toAmount(a?.price)),
      mobile: "sheet",
    },
    {
      key: "amount",
      label: "Cost",
      width: 120,
      align: "right",
      render: cells.money((a) => toAmount(a?.amount), true),
      mobile: "trailing",
    },
  ];

  const rowActions: ListRowAction<AssetRow>[] = [
    {
      key: "edit",
      label: "Edit",
      icon: <EditIconV2 />,
      sheetIcon: "inventory",
      onRun: (a) => goToAsset(String(a.id)),
    },
  ];

  return (
    <div
      className="h-full "
      style={{
        overflowY: isMobile ? "auto" : undefined,
        paddingBottom: isMobile ? "24px" : undefined,
        display: isMobile ? undefined : "flex",
        flexDirection: isMobile ? undefined : "column",
      }}
    >
      <div className="">
        <div className="lg:flex items-center justify-between">
          <p
            style={{ color: "#222525" }}
            className="text-[24px] leading-[130%] font-medium font-inter"
          >
            {language && language?.["Assets"] ? language?.["Assets"] : "Assets"}
          </p>
          <div className="flex items-center justify-between">
            <div
              style={{
                padding: "10px 24px",
                backgroundColor: "#EBF0F0",
                borderRadius: "40px",
                width: "fit-content",
                marginTop: isMobile ? "20px" : "0",
              }}
              className="items-center justify-center w-fit cursor-pointer relative button_ledger"
            >
              <div className="w-full flex items-center gap-x-2">
                <UploadIcon />
                <p
                  style={{ color: "#0F7679", fontWeight: "500" }}
                  className="text-[14px] leading-[20px] text-main font-inter"
                >
                  {language && language?.["Export this month asset value"]
                    ? language?.["Export this month asset value"]
                    : "Export this month asset value"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ListScreen
        language={language}
        columns={columns}
        fetchRows={fetchAssets}
        searchPlaceholder="Search assets"
        searchInputRef={searchInputRef}
        rowActions={rowActions}
        countNoun="assets"
        emptyState={inforTable}
      />
    </div>
  );
};

export default Assets;
